import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { SessionCredentials, SessionStateDto } from '@filmsloth/shared';
import { api, type SessionPreview } from '../api/client.js';
import { QrScanner } from '../components/QrScanner.js';
import { useT } from '../i18n/index.js';
import { haptic } from '../lib/haptics.js';
import { qrScanningSupported } from '../lib/qrScan.js';
import { readLastName, rememberName } from '../lib/lastName.js';
import { useAccount } from '../session/useAccount.js';

interface JoinScreenProps {
  initialCode: string;
  onBack: () => void;
  onJoined: (credentials: SessionCredentials, state: SessionStateDto) => void;
}

const CODE_LENGTH = 6;
/** Matches the name field's own `maxLength`. */
const NAME_MAX_LENGTH = 24;

export function JoinScreen({ initialCode, onBack, onJoined }: JoinScreenProps) {
  const t = useT();
  const account = useAccount();
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [name, setName] = useState(() => readLastName(NAME_MAX_LENGTH));
  const nameRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<SessionPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Asked once: the answer cannot change while this screen is open, and
  // asking inside the render would re-check it on every keystroke.
  const [canScan] = useState(qrScanningSupported);

  // Signed in people get their username here for free instead of typing a
  // name again — still just a starting point, since the same person picks
  // different names in different sessions.
  useEffect(() => {
    if (account.account && name.length === 0) {
      setName(account.account.username.slice(0, NAME_MAX_LENGTH));
    }
    // Keyed on the username alone: `name` is read only to avoid clobbering
    // something already typed, and listing it would re-run this on every
    // keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.account?.username]);

  // Look the session up as soon as the code is complete, so people see what
  // they are joining before they type their name.
  useEffect(() => {
    if (code.length !== CODE_LENGTH) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    api
      .previewSession(code)
      .then((result) => {
        if (!cancelled) {
          setPreview(result);
          setError(null);
          // The code is done, so the next thing to fill in is the name. Not
          // when it is already there: a keyboard popping up for a field that
          // needs nothing would only cover the button.
          if (nameRef.current && nameRef.current.value.trim() === '') nameRef.current.focus();
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreview(null);
          setError(t('join.notFound'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, t]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { credentials, state } = await api.joinSession(code, name);
      rememberName(name);
      onJoined(credentials, state);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('error.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="screen" onSubmit={submit}>
      <div className="row row--between">
        <h1>{t('join.title')}</h1>
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="join-code">
          {t('join.code')}
        </label>
        <input
          id="join-code"
          className="input input--code"
          value={code}
          onChange={(event) =>
            setCode(
              event.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
                .slice(0, CODE_LENGTH),
            )
          }
          placeholder={t('join.codePlaceholder')}
          autoComplete="off"
          autoCapitalize="characters"
          autoFocus={initialCode.length === 0}
          inputMode="text"
          maxLength={CODE_LENGTH}
        />
      </div>

      {/* Offered only where the browser can really do it — see `qrScan.ts`.
          A button that opens a camera and then reads nothing is worse than
          no button, and typing six characters was never the hard part. */}
      {canScan ? (
        scanning ? (
          <QrScanner
            onClose={() => setScanning(false)}
            onCode={(scanned) => {
              setCode(scanned);
              setScanning(false);
              // The one moment in this flow where something happened that the
              // person did not do with their thumb.
              haptic('like');
            }}
          />
        ) : (
          <button type="button" className="btn btn--block" onClick={() => setScanning(true)}>
            {t('join.scan')}
          </button>
        )
      ) : null}

      {preview ? (
        <div className="panel stack stack--tight">
          {preview.hostName ? (
            <strong>{t('join.hostedBy', { name: preview.hostName })}</strong>
          ) : null}
          <span className="muted small">
            {t('join.peopleInside', { count: preview.participantCount })}
          </span>
        </div>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="join-name">
          {t('join.yourName')}
        </label>
        <input
          id="join-name"
          ref={nameRef}
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('create.namePlaceholder')}
          maxLength={NAME_MAX_LENGTH}
          autoComplete="nickname"
        />
      </div>

      {error ? <p className="banner banner--error">{error}</p> : null}

      <button
        type="submit"
        className="btn btn--primary btn--lg btn--block"
        disabled={busy || code.length !== CODE_LENGTH || name.trim().length === 0}
      >
        {busy ? t('common.loading') : t('join.submit')}
      </button>
    </form>
  );
}
